import {
    Grid,
    SelectChangeEvent,
    Theme,
    Typography
} from "@mui/material";
import MFSelectField from "../../../lib/formik/SelectField";
import { MFTextField } from "../../../lib/formik";
import {
    useDispatch,
    useSelector
} from "react-redux";
import { RootStateType } from "../../../redux-store/reducers";
import {
    GetBankBalanceResponseBody,
    GetOrderEntryBanksListResponseBody, GetOrderEntryInvestorListResponseBody
} from "../../../redux-store/types/orderManagement";
import {
    FormikContextType,
    useFormikContext
} from '../../../lib/formik/Formik';
import { CurrencyConversionToWords, CustomTooltip } from "./commonComponents";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { getBankBalance } from "../../../redux-store/actions/orderManagement";
import { showError } from "../../../redux-store/actions/auth";
import {
    Dispatch,
    SetStateAction,
    useRef
} from "react";
import { AVAILABLE_BANK_BALANCE_DESCRIPTION, BANK_SUB_ACCOUNT_TYPES, MPOWER_ACCOUNT_TYPE_MASTER, NET_AVAILABLE_BALANCE_DESCRIPTION, UTILIZED_BALANCE_DESCRIPTION } from "../../../utils/constant";

interface PortfolioDetails {
    portfolioName: string;
    portfolioId: string;
    accountType: string;
    email: string;
    exchange: string;
    modeOfHolding: string;
    portfolioCcy: string;
    portfolioNo: number;
}

export default function ClientDetailsForm({
    investorData,
    activeProductType,
    handleClientNameChange,
    bankAccountList,
    handleAccountChange,
    setLoadingStatus,
    isLoading
}: {
    investorData: GetOrderEntryInvestorListResponseBody;
    activeProductType: string;
    handleClientNameChange: (selectedObj: Record<string, any>) => void;
    bankAccountList: GetOrderEntryBanksListResponseBody
    handleAccountChange: (event: SelectChangeEvent<unknown>) => void;
    setLoadingStatus: Dispatch<SetStateAction<"" | "apiLoading" | "formSubmitting">>;
    isLoading: boolean;
}): JSX.Element {
    const investorBankAccountRef = useRef<HTMLElement>(null)

    const dispatch = useDispatch();

    const { currencySymbol, locale = "en-IN" } = useSelector((store: RootStateType) => store.currencyInfo);

    const {
        values,
        resetForm,
        setFieldValue
    }: FormikContextType<Record<string, any>> = useFormikContext();

    const isAccountTypePoa = [MPOWER_ACCOUNT_TYPE_MASTER["POA"]].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${values?.accountType ?? ""}`.toUpperCase()])

    const handleInvestorBankAccountChange = async (event: SelectChangeEvent<unknown>) => {
        try {
            setLoadingStatus("apiLoading")

            const selectedInvestorBankAccountObj = (bankAccountList?.bankDetails ?? [])?.find(obj => `${obj?.bankAccNo ?? ""}` === event.target.value)

            await setFieldValue("investorBankAccount", selectedInvestorBankAccountObj ?? null)

            let getBankBalanceResponse: GetBankBalanceResponseBody | null = null

            if (isAccountTypePoa) {
                getBankBalanceResponse = await (dispatch(getBankBalance({
                    investorBankAccount: event.target.value as string ?? "",
                    clientCode: `${values?.clientName?.clientCode ?? ""}`,
                    investorIfscCode: `${selectedInvestorBankAccountObj?.ifscCode ?? ""}`
                })) as unknown as Promise<GetBankBalanceResponseBody>);

                if (!getBankBalanceResponse?.isSuccess) {
                    dispatch(showError(getBankBalanceResponse?.message ?? "No Data Available"))
                }
            }

            resetForm({
                values: {
                    ...values,
                    investorBankAccount: selectedInvestorBankAccountObj ?? null,
                    bankSubAccountType: selectedInvestorBankAccountObj?.subAccountType ?? "",
                    availableBankBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.bankBalance ?? 0)) ? Number(getBankBalanceResponse?.bankBalance ?? 0) : 0)?.toLocaleString(locale)
                        : "Not Applicable",
                    utilizedBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.utilizedAmount ?? 0)) ? Number(getBankBalanceResponse?.utilizedAmount ?? 0) : 0)?.toLocaleString(locale)
                        : "Not Applicable",
                    netAvailableBalance: isAccountTypePoa ?
                        (!Number.isNaN(Number(getBankBalanceResponse?.netAmount ?? 0)) ? Number(getBankBalanceResponse?.netAmount ?? 0) : 0)?.toLocaleString(locale)
                        : "Not Applicable",
                }
            })
        } catch (error) {
            console.log((error as Error)?.message ?? "");
        } finally {
            setLoadingStatus("")
            setTimeout(() => {
                investorBankAccountRef.current?.focus()
            }, 0)
        }
    }

    const refactoredAvailableBankBalance = isAccountTypePoa ? `${values?.availableBankBalance ?? ""}`?.replaceAll(',', '') : (values?.availableBankBalance ?? "")
    const refactoredUtilizedBalance = isAccountTypePoa ? `${values?.utilizedBalance ?? ""}`?.replaceAll(',', '') : (values?.utilizedBalance ?? "")
    const refactoredNetAvailableBalance = isAccountTypePoa ? `${values?.netAvailableBalance ?? ""}`?.replaceAll(',', '') : (values?.netAvailableBalance ?? "")

    return (
        <Grid
            container
            columnSpacing={3}
        >
            <Grid item xs={12} lg={4}>
                <FormikSearchDropdown
                    name="clientName"
                    label="Client Name *"
                    options={(Array.isArray(investorData?.result) ? investorData?.result : [])?.map((obj) => ({
                        ...obj,
                        label: `${obj?.customerName ?? ""} ${obj?.clientCode ? "(" + obj?.clientCode + ")" : ""} ${obj?.investorType ? "(" + obj?.investorType + ")" : ""} ${`${obj?.isFamilyHead ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""} - ${obj?.portfolios?.[0]?.accountType ?? ""}`,
                        value: obj?.clientCode,
                    })) ?? []}
                    onChange={handleClientNameChange}
                    disabled={isLoading}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    name="account"
                    label="Account *"
                    items={(values?.clientName?.portfolios ?? [])?.map((obj: PortfolioDetails) => ({
                        key: `${obj?.portfolioName ?? ""} (${obj?.portfolioId ?? ""})`,
                        value: obj?.portfolioId ?? "",
                    }))}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleAccountChange}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="accountType"
                    label="Account Type"
                    placeholder=""
                    disabled={true}
                    value={values?.accountType ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="modeOfHolding"
                    label="Mode Of Holding"
                    placeholder=""
                    disabled={true}
                    value={`${values?.modeOfHolding ?? ""}`.toUpperCase()}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFSelectField
                    inputRef={investorBankAccountRef}
                    name="investorBankAccount"
                    label="Investor Bank Account *"
                    items={bankAccountList?.bankDetails?.map(obj => ({
                        key: `
                        ${obj?.bankAccNo ?? ""} 
                        ${obj?.bankname ?? ""} 
                        ${BANK_SUB_ACCOUNT_TYPES[`${obj?.subAccountType ?? ""}`.toUpperCase()] ? "(" + BANK_SUB_ACCOUNT_TYPES[`${obj?.subAccountType ?? ""}`.toUpperCase()] + ")" : ""}
                        `,
                        value: obj?.bankAccNo,
                    })) ?? []}
                    disabled={isLoading || !values?.clientName}
                    onChange={handleInvestorBankAccountChange}
                    value={`${values?.investorBankAccount?.bankAccNo ?? ""}`}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="bankSubAccountType"
                    label="Investor Bank Sub Account Type"
                    placeholder=""
                    disabled
                    value={`${BANK_SUB_ACCOUNT_TYPES[`${values?.bankSubAccountType ?? ""}`.toUpperCase()] ?? ""}`}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="emailId"
                    label="Email ID"
                    placeholder=""
                    disabled={true}
                    value={values?.emailId ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="srm"
                    label="RM"
                    placeholder=""
                    disabled
                    value={values?.srm ?? ""}
                />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="availableBankBalance"
                    label="Available Bank Balance"
                    placeholder=""
                    startAdornment={(isAccountTypePoa && values?.availableBankBalance) ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    endAdornment={
                        <CustomTooltip
                            titleContent={
                                <Typography variant='subtitle_600'>
                                    {AVAILABLE_BANK_BALANCE_DESCRIPTION}
                                </Typography>
                            }
                        />
                    }
                    disabled={true}
                    formatCurrency={isAccountTypePoa}
                    value={values?.availableBankBalance ?? ""}
                />
                <CurrencyConversionToWords value={refactoredAvailableBankBalance} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="utilizedBalance"
                    label="Utilized Balance"
                    placeholder=""
                    startAdornment={(isAccountTypePoa && values?.utilizedBalance) ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    endAdornment={
                        <CustomTooltip
                            titleContent={
                                <Typography variant='subtitle_600' >
                                    {UTILIZED_BALANCE_DESCRIPTION}
                                </Typography>
                            }
                        />
                    }
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        },
                    }}
                    disabled={true}
                    formatCurrency={isAccountTypePoa}
                    value={values?.utilizedBalance ?? ""}
                />
                <CurrencyConversionToWords value={refactoredUtilizedBalance} />
            </Grid>
            <Grid item xs={12} lg={4}>
                <MFTextField
                    name="netAvailableBalance"
                    label="Net Available Balance"
                    placeholder=""
                    startAdornment={(isAccountTypePoa && values?.netAvailableBalance) ? (
                        <Typography
                            variant='body_500'
                            sx={(theme: Theme) => ({ color: theme.palette.text.secondary })}
                        >
                            {String.fromCharCode(parseInt(currencySymbol, 16))}
                        </Typography>
                    ) : undefined}
                    endAdornment={
                        <CustomTooltip
                            titleContent={
                                <Typography variant='subtitle_600'>
                                    {NET_AVAILABLE_BALANCE_DESCRIPTION}
                                </Typography>
                            }
                        />
                    }
                    sx={{
                        '&.MuiOutlinedInput-root': {
                            "& fieldset>legend": {
                                fontSize: "0.78em",
                            }
                        }
                    }}
                    disabled={true}
                    formatCurrency={isAccountTypePoa}
                    value={values?.netAvailableBalance ?? ""}
                />
                <CurrencyConversionToWords value={refactoredNetAvailableBalance} />
            </Grid>
            {(activeProductType === "Bonds") && (
                <>
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="uccCode"
                            label="UCC Code"
                            placeholder=""
                            disabled={true}
                            value={values?.uccCode ?? ""}
                        />
                    </Grid>
                    {/* TODO (BP-03): DP ID field in client details is temporarily commented out.
                        DP ID is now surfaced in the Settlement Details section instead.
                        Remove entirely if this placement is permanently dropped.
                    <Grid item xs={12} lg={4}>
                        <MFTextField
                            name="dpId"
                            label="DP ID"
                            placeholder=""
                            disabled={true}
                            value={values?.dpId ?? ""}
                        />
                    </Grid> */}
                </>
            )}
        </Grid>
    )
}